import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Newsletter from '@/components/sections/Newsletter'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { 
  useEvent, 
  useFilterEvents, 
  useEventComments, 
  useAddEventComment, 
  useAddEventReply 
} from '@/lib/api/hooks/event'
import { 
  generateGoogleCalendarUrl, 
  generateOutlookCalendarUrl, 
  downloadICalFile,
  openCalendar,
  type CalendarEvent 
} from '@/utils/calendar'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'

export default function EventDetail() {
  const { eventId } = useParams<{ eventId: string }>()
  const [selectedAdditionalImageIndex, setSelectedAdditionalImageIndex] = useState<number | null>(null)
  const [commentForm, setCommentForm] = useState({
    name: '',
    email: '',
    comment: ''
  })
  const [replyForms, setReplyForms] = useState<Record<string, { name: string; email: string; reply: string }>>({})
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null)
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set())

  // Fetch event details
  const { data: eventData, isLoading, error } = useEvent(eventId)
  const eventDetail = eventData?.data

  // Fetch other events for sidebar (exclude current event)
  const { data: otherEventsData } = useFilterEvents(
    { page: 1, size: 4 },
    { upcoming: true }
  )

  // Fetch comments and replies
  const { data: commentsData, isLoading: commentsLoading } = useEventComments(eventId)
  
  // Mutations
  const addComment = useAddEventComment(eventId || '')
  const addReply = useAddEventReply(eventId || '')

  // Get other events and filter out current event
  const otherEvents = (otherEventsData?.data?.data || [])
    .filter(event => event.eventId !== eventId)
    .slice(0, 2)

  // Get comments with replies
  const commentsWithReplies = commentsData?.data?.[0]?.eventReplyResponseDetails || []

  const additionalImageUrls = eventDetail?.additionalImageUrls || []
  const additionalFileUrls = eventDetail?.additionalFileUrls || []

  // Format date helper
  const formatDate = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return dateTimeString
    }
  }

  const formatShortDate = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString)
      const day = date.getDate()
      const month = date.toLocaleDateString('en-GB', { month: 'short' })
      const year = date.getFullYear().toString().slice(-2)
      return `${day} ${month} '${year}`
    } catch {
      return dateTimeString
    }
  }

  const getFileNameFromUrl = (url: string): string => {
    const cleaned = url.split('?')[0].split('#')[0]
    const name = cleaned.split('/').pop()
    return name && name.trim() ? decodeURIComponent(name) : 'document'
  }

  const openAdditionalImage = (index: number) => setSelectedAdditionalImageIndex(index)
  const closeAdditionalImage = () => setSelectedAdditionalImageIndex(null)
  const showPrevAdditionalImage = () => {
    if (!additionalImageUrls.length || selectedAdditionalImageIndex === null) return
    const nextIndex = (selectedAdditionalImageIndex - 1 + additionalImageUrls.length) % additionalImageUrls.length
    setSelectedAdditionalImageIndex(nextIndex)
  }
  const showNextAdditionalImage = () => {
    if (!additionalImageUrls.length || selectedAdditionalImageIndex === null) return
    const nextIndex = (selectedAdditionalImageIndex + 1) % additionalImageUrls.length
    setSelectedAdditionalImageIndex(nextIndex)
  }

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!eventId) return

    try {
      await addComment.mutateAsync({
        name: commentForm.name,
        email: commentForm.email,
        comment: commentForm.comment
      })
      
      // Reset form
      setCommentForm({
        name: '',
        email: '',
        comment: ''
      })
    } catch (error) {
      console.error('Error submitting comment:', error)
    }
  }

  const handleReplySubmit = async (commentId: string) => {
    const replyForm = replyForms[commentId]
    if (!replyForm || !eventId) return

    try {
      await addReply.mutateAsync({
        commentId,
        replyData: {
          name: replyForm.name,
          email: replyForm.email,
          reply: replyForm.reply
        }
      })
      
      // Reset reply form
      setReplyForms(prev => {
        const newForms = { ...prev }
        delete newForms[commentId]
        return newForms
      })
      setActiveReplyId(null)
    } catch (error) {
      console.error('Error submitting reply:', error)
    }
  }

  const toggleReplyForm = (commentId: string) => {
    if (activeReplyId === commentId) {
      setActiveReplyId(null)
    } else {
      setActiveReplyId(commentId)
      // Initialize reply form if it doesn't exist
      if (!replyForms[commentId]) {
        setReplyForms(prev => ({
          ...prev,
          [commentId]: { name: '', email: '', reply: '' }
        }))
      }
    }
  }

  const toggleCommentExpansion = (commentId: string) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev)
      if (newSet.has(commentId)) {
        newSet.delete(commentId)
      } else {
        newSet.add(commentId)
      }
      return newSet
    })
  }

  // Calendar functionality
  const [showCalendarMenu, setShowCalendarMenu] = useState(false)

  const handleAddToCalendar = (type: 'google' | 'outlook' | 'ical') => {
    if (!eventDetail) return

 
    const calendarEvent: CalendarEvent = {
      title: eventDetail.eventTitle,
      description: eventDetail.description.replace(/<[^>]*>/g, ''), // Strip HTML tags
      location: eventDetail.eventVenue,
      startDate: new Date(eventDetail.startDate)
    }

    if(eventDetail.endDate) {
      calendarEvent.endDate = new Date(eventDetail.endDate)
    }

    if(eventDetail.startDate && eventDetail.endDate) {
      calendarEvent.duration = (new Date(eventDetail.endDate).getTime() - new Date(eventDetail.startDate).getTime()) / (1000 * 60 * 60)
    }

    switch (type) {
      case 'google':
        openCalendar(generateGoogleCalendarUrl(calendarEvent))
        break
      case 'outlook':
        openCalendar(generateOutlookCalendarUrl(calendarEvent))
        break
      case 'ical':
        downloadICalFile(calendarEvent)
        break
    }
    
    setShowCalendarMenu(false)
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Events', href: '/events' },
    { label: eventDetail?.eventTitle || 'Event Details', href: '', active: true }
  ]

  // Loading state
  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading event details..." />
  }

  // Error state
  if (error || !eventDetail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-500">Error loading event details. Please try again later.</p>
          <Link to="/events" className="mt-4 inline-block text-wine-100 hover:text-wine-200">
            Back to Events
          </Link>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="min-h-screen bg-white"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="max-w-[1440px] mx-auto px-4 md:px-20" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Main Content */}
      <motion.div className="max-w-[1440px] mx-auto px-4 md:px-20 pb-16 w-full" variants={fadeUp}>
        <motion.div
          className="flex flex-col lg:flex-row gap-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportReveal}
        >
          {/* Main Content */}
          <div className="flex-1">
            {/* Event Header */}
            <div className="mb-8">
              <div className="flex items-start justify-between mb-2">
                <h1 className="text-2xl font-bold text-gray-900 font-['PT_Serif'] flex-1">
                  {eventDetail.eventTitle}
                </h1>
                {/* Add to Calendar Button */}
                <div className="relative ml-4">
                  <button
                    onClick={() => setShowCalendarMenu(!showCalendarMenu)}
                    className="flex items-center gap-2 px-4 py-2 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors text-sm font-medium"
                  >
                    <Calendar className="w-4 h-4" />
                    Add to Calendar
                  </button>
                  
                  {showCalendarMenu && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowCalendarMenu(false)}
                      />
                      <div 
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleAddToCalendar('google')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Google Calendar
                        </button>
                        <button
                          onClick={() => handleAddToCalendar('outlook')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Outlook Calendar
                        </button>
                        <button
                          onClick={() => handleAddToCalendar('ical')}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Download .ics file
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col gap-2 text-sm text-gray-600 mb-6">
                <p><strong>When:</strong> {eventDetail.datePublished ? formatDate(eventDetail.datePublished) : ""}</p>
                <p><strong>Where:</strong> {eventDetail.eventVenue}</p>
              </div>
            </div>

            {/* Featured Image */}
            <div className="mb-8">
              <img 
                src={eventDetail.imageUrl} 
                alt={eventDetail.eventTitle}
                className="w-full h-64 md:h-96 object-cover rounded-lg"
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                }}
              />
            </div>

            {/* Event Description */}
            <div className="mb-5">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 font-['PT_Serif']">About This Event</h2>
              <div 
                className="prose max-w-none text-lg leading-relaxed text-gray-900"
                dangerouslySetInnerHTML={{ __html: eventDetail.description }}
              />
            </div>

            {(additionalImageUrls.length > 0 || additionalFileUrls.length > 0) && (
              <div className="mb-5 flex flex-col gap-6">
                {additionalImageUrls.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <h2 className="text-2xl font-bold text-gray-900 font-['PT_Serif']">ADDITIONAL IMAGES</h2>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {additionalImageUrls.map((url, index) => (
                        <button
                          key={`${url}-${index}`}
                          type="button"
                          onClick={() => openAdditionalImage(index)}
                          className="shrink-0 rounded-lg overflow-hidden border border-gray-200 hover:border-wine-100 transition-colors"
                          aria-label={`Open additional image ${index + 1}`}
                        >
                          <img src={url} alt={`Additional image ${index + 1}`} className="w-36 h-24 object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {additionalFileUrls.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <h2 className="text-2xl font-bold text-gray-900 font-['PT_Serif']">ADDITIONAL FILES</h2>
                    <ul className="flex flex-col gap-2">
                      {additionalFileUrls.map((url, index) => (
                        <li key={`${url}-${index}`}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-base text-wine-100 hover:text-wine-200 hover:underline break-all"
                          >
                            {getFileNameFromUrl(url)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Comments Section */}
            <div className="mb-5">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 font-['PT_Serif']">
                COMMENTS ({commentsWithReplies.length})
              </h2>

              {/* Display Comments */}
              {commentsLoading ? (
                <LoadingSpinner size="sm" message="Loading comments..." className="py-8" />
              ) : commentsWithReplies.length > 0 ? (
                <div className="space-y-6 mb-8">
                  {commentsWithReplies.map((item) => {
                    const comment = item.eventCommentResponse
                    const replies = item.eventReplyResponses || []
                    const isExpanded = expandedComments.has(comment.id)
                    const showReplies = isExpanded || replies.length === 0

                    return (
                      <div key={comment.id} className="border-b border-gray-200 pb-6">
                        {/* Comment */}
                        <div className="flex flex-col gap-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-bold text-gray-900">{comment.name}</span>
                                <span className="text-sm text-gray-500">
                                  {new Date(comment.dateCommented).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                              <p className="text-gray-700 mb-3">{comment.comment}</p>
                            </div>
                          </div>

                          {/* Reply Button & Count */}
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => toggleReplyForm(comment.id)}
                              className="text-wine-100 text-sm font-medium hover:text-wine-200 transition-colors"
                            >
                              {activeReplyId === comment.id ? 'Cancel Reply' : 'Reply'}
                            </button>
                            
                            {replies.length > 0 && (
                              <button
                                onClick={() => toggleCommentExpansion(comment.id)}
                                className="flex items-center gap-1 text-gray-600 text-sm hover:text-gray-900 transition-colors"
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp className="w-4 h-4" />
                                    Hide {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-4 h-4" />
                                    Show {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                                  </>
                                )}
                              </button>
                            )}
                          </div>

                          {/* Reply Form */}
                          {activeReplyId === comment.id && (
                            <div className="ml-8 mt-4 bg-gray-50 p-4 rounded-lg">
                              <form onSubmit={(e) => {
                                e.preventDefault()
                                handleReplySubmit(comment.id)
                              }} className="space-y-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-900 mb-2">Your Reply</label>
                                  <textarea
                                    value={replyForms[comment.id]?.reply || ''}
                                    onChange={(e) => setReplyForms(prev => ({
                                      ...prev,
                                      [comment.id]: { ...prev[comment.id], reply: e.target.value }
                                    }))}
                                    className="w-full h-24 bg-white border border-gray-300 rounded p-3 resize-none text-sm"
                                    required
                                  />
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <input
                                    type="text"
                                    placeholder="Your Name"
                                    value={replyForms[comment.id]?.name || ''}
                                    onChange={(e) => setReplyForms(prev => ({
                                      ...prev,
                                      [comment.id]: { ...prev[comment.id], name: e.target.value }
                                    }))}
                                    className="w-full h-10 bg-white border border-gray-300 rounded px-3 text-sm"
                                    required
                                  />
                                  <input
                                    type="email"
                                    placeholder="Your Email"
                                    value={replyForms[comment.id]?.email || ''}
                                    onChange={(e) => setReplyForms(prev => ({
                                      ...prev,
                                      [comment.id]: { ...prev[comment.id], email: e.target.value }
                                    }))}
                                    className="w-full h-10 bg-white border border-gray-300 rounded px-3 text-sm"
                                    required
                                  />
                                </div>
                                <button
                                  type="submit"
                                  disabled={addReply.isPending}
                                  className="px-6 py-2 bg-wine-100 text-white text-sm rounded font-bold hover:bg-wine-200 transition-colors disabled:opacity-50"
                                >
                                  {addReply.isPending ? 'Posting...' : 'Post Reply'}
                                </button>
                              </form>
                            </div>
                          )}

                          {/* Replies */}
                          {showReplies && replies.length > 0 && (
                            <div className="ml-8 mt-4 space-y-4">
                              {replies.map((reply) => (
                                <div key={reply.id} className="bg-gray-50 p-4 rounded-lg">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-semibold text-gray-900">{reply.responderName}</span>
                                    <span className="text-xs text-gray-500">
                                      {new Date(reply.dateReplied).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <p className="text-gray-700 text-sm">{reply.response}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              )}

              {/* Leave a Comment Form */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 font-['PT_Serif']">LEAVE A COMMENT</h3>
                <p className="text-base text-gray-600 mb-6">Your email address will not be published</p>

                <form onSubmit={handleCommentSubmit} className="space-y-6">
                  {/* Comment Field */}
                  <div>
                    <label className="block text-base font-medium text-gray-900 mb-2">Comment</label>
                    <textarea
                      value={commentForm.comment}
                      onChange={(e) => setCommentForm({...commentForm, comment: e.target.value})}
                      className="w-full h-32 bg-gray-50 border border-gray-300 rounded p-4 resize-none"
                      required
                    />
                  </div>

                  {/* Name and Email Fields */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-base font-medium text-gray-900 mb-2">Name</label>
                      <input
                        type="text"
                        value={commentForm.name}
                        onChange={(e) => setCommentForm({...commentForm, name: e.target.value})}
                        className="w-full h-12 bg-gray-50 border border-gray-300 rounded px-4"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-gray-900 mb-2">Email Address</label>
                      <input
                        type="email"
                        value={commentForm.email}
                        onChange={(e) => setCommentForm({...commentForm, email: e.target.value})}
                        className="w-full h-12 bg-gray-50 border border-gray-300 rounded px-4"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={addComment.isPending}
                    className="px-8 py-3 bg-wine-100 text-white rounded font-bold hover:bg-wine-200 transition-colors disabled:opacity-50"
                  >
                    {addComment.isPending ? 'Posting...' : 'Post Comment'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full md:w-[400px]">
            {/* Other Events */}
            {otherEvents.length > 0 && (
              <div className="bg-white mb-8">
                <h3 className="text-2xl font-normal font-['PT_Serif'] text-gray-900 mb-6">OTHER EVENTS</h3>
                <div className="flex flex-col gap-8">
                  {otherEvents.map((event) => (
                    <Link
                      key={event.eventId}
                      to={`/events/${event.eventId}`}
                      className="flex flex-col group shadow-sm hover:shadow-md transition-shadow"
                    >
                      <img 
                        src={event.imageUrl} 
                        alt={event.eventTitle}
                        className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                        }}
                      />
                      <div className="px-4 py-6 bg-gray-50 flex flex-col gap-4">
                        <h4 className="text-lg leading-relaxed text-gray-900 font-['PT_Serif'] group-hover:text-wine-100 transition-colors">
                          {event.eventTitle}
                        </h4>
                        <span className="text-base font-medium text-gray-900">{event.datePublished ? formatShortDate(event.datePublished) : ""}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Newsletter Signup */}
            <div className="bg-gray-50 p-6 ">
              <Newsletter />
            </div>
          </div>
        </motion.div>
      </motion.div>

      {selectedAdditionalImageIndex !== null && additionalImageUrls.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <button
            type="button"
            onClick={closeAdditionalImage}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Close image viewer"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={showPrevAdditionalImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <img
            src={additionalImageUrls[selectedAdditionalImageIndex]}
            alt={`Additional image ${selectedAdditionalImageIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
          />

          <button
            type="button"
            onClick={showNextAdditionalImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white hover:bg-white/30"
            aria-label="Next image"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </motion.div>
  )
}